import * as React from 'react';
import { IAddWidgetDialogProps } from './AddWidgetDialog.properties';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { IActionItem } from 'components/common/Modals/FormViewModal.properties';
import AddWidgetForm from './AddWidgetDialog.form';
import { useDMEContextSelector } from '../DashboardMaintenanceEnquiry.context';
import { useAddWidget, useGetDMEDashboardDetailsQueryData, useSetDashboardLayoutWidgetsQueryData } from 'api/dashboardMaintenanceEnquiry/dashboardMaintenanceEnquiry';
import { Inline, objectify } from 'api/utils';
import { ISelectFieldHandle } from '@markinson/uicomponents-v2/SelectField/SelectField.properties';

const AddWidgetDialog = (props: IAddWidgetDialogProps): JSX.Element => {
  const { open, onClose, values, initialValues, valuesSchema, selectedDashboardId, selectedDashboardType, changeFormFieldValue } = props;
  const params = new URLSearchParams(location.search);
  const dashboardId = useDMEContextSelector<'DashboardId'>((state) => state.DashboardId) || params.get('DashboardId');
  const [isValidDashboardWidget, setIsValidDashboardWidget] = React.useState<boolean>(false);
  const [selectedWidgetTypeId, setSelectedWidgetTypeId] = React.useState<number | undefined>(undefined);
  const { mutateAsync: addWidgetMutateAsync, isLoading: isProcessing } = useAddWidget();
  const setDashboardLayoutWidgetsQueryData = useSetDashboardLayoutWidgetsQueryData();
  const getDashboardDetails = useGetDMEDashboardDetailsQueryData();
  const cachedDashboardDetails = getDashboardDetails(selectedDashboardId);
  const layoutPanelRef = React.useRef<ISelectFieldHandle>(null);

  const dashboardType = React.useMemo(
      () => {
        return (cachedDashboardDetails as any)?.DashboardDetail?.inlineObject?.DashboardType ?? selectedDashboardType;
      },
      [cachedDashboardDetails, selectedDashboardType]
    );

  const confirmationActions = (): IActionItem[] => {
    return [
      {
        title: 'Add',
        isDefault: true,
        disabled: isProcessing,
        listener: async () => {
          if (values?.WidgetId && values?.LayoutPanel) {
            const response = await addWidgetMutateAsync({
              ...values,
              DashboardId: dashboardId,
              WidgetId: Number(values?.WidgetId)
            });

            if (response?.Status) {
              setDashboardLayoutWidgetsQueryData(Number(dashboardId), {
                DashboardWidgets: response?.DashboardWidgets?.map((o) => objectify(o as Inline<any>)) || [],
                Status: response?.Status
              });
              onClose();
            }
          } else {
            if (!values?.LayoutPanel) {
              layoutPanelRef.current?.validate();
            }
          }
        }
      },
      {
        title: 'Cancel',
        listener: () => {
          onClose();
        }
      }
    ];
  };

  return (<FormViewModal
    open={open}
    loading={false}
    title='Add Widget'
    formProps={{
      style: {
        height: '395px',
        minWidth: '450px'
      },
      dashboardType: dashboardType,
      formSchema: AddWidgetForm,
      initialValues: initialValues || {},
      valuesSchema: valuesSchema,
      fieldExtensions: {
        WidgetId: {
          onSelectedItemChange: (value: any) => {
            changeFormFieldValue('LayoutPanel', '');
            setIsValidDashboardWidget(!!value?.Code);
            setSelectedWidgetTypeId(value?.Details?.WidgetTypeId);
          }
        },
        LayoutPanel: {
          disabled: !(values?.WidgetId && selectedWidgetTypeId && isValidDashboardWidget),
          params: { WidgetTypeId: Number(selectedWidgetTypeId) || 0 },
          innerRef: (Ref) => layoutPanelRef.current = Ref,
        },
      }
    }}
    actions={confirmationActions()}
    dialogActionsButtons={true}
    dialogActionsShadow={false}

  />);
};

export default AddWidgetDialog;
